/**
 * NewFlowModal — entry point for creating a new integration flow.
 *
 * Two columns: starting point (left) + flow metadata (right). Mirrors
 * the Pipebase platform design.
 *
 * v1 supports only the "Blank flow" starter — clone/openapi/template
 * are visible as future-roadmap cards, with a tiny "coming soon"
 * footnote when selected. Picking blank + a name + Create takes the
 * user straight into the next-editor.
 *
 * The "From template" / "Clone existing" / "From OpenAPI" placeholders
 * are intentional: shipping the full flow would require backend
 * scaffolding work that's out of this PR's scope. The modal still
 * communicates the intended IA so users know what's coming.
 */
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../api';
import { useToast } from './Toast';
import { useCategories } from '../hooks/useCategories';

type Starter = 'blank' | 'template' | 'openapi' | 'clone';

interface StarterDef {
  id: Starter;
  title: string;
  desc: string;
  available: boolean;
}

const STARTERS: StarterDef[] = [
  {
    id: 'blank',
    title: 'Blank flow',
    desc: 'Start from an empty canvas. Pick triggers and targets manually.',
    available: true,
  },
  {
    id: 'template',
    title: 'From template',
    desc: 'Fork a curated pattern from the Marketplace.',
    available: false,
  },
  {
    id: 'openapi',
    title: 'From OpenAPI / WSDL',
    desc: 'Paste a spec URL. We generate triggers, schemas and a stub mapping.',
    available: false,
  },
  {
    id: 'clone',
    title: 'Clone existing',
    desc: 'Duplicate one of your flows, including connections and variables.',
    available: false,
  },
];

export interface NewFlowModalProps {
  onClose: () => void;
}

export function NewFlowModal({ onClose }: NewFlowModalProps) {
  const navigate = useNavigate();
  const { push } = useToast();
  const { categories } = useCategories();
  const [starter, setStarter] = useState<Starter>('blank');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<string>(categories[0] ?? '');
  const [creating, setCreating] = useState(false);

  const canCreate = name.trim().length >= 3 && starter === 'blank' && !creating;

  const create = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!canCreate) return;
    setCreating(true);
    try {
      const created = await api.createFlow({ name: name.trim() });
      push('success', `Created "${created.name}"`);
      onClose();
      navigate(`/edit-next/${encodeURIComponent(created.id)}`);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Failed to create flow';
      push('error', msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="modal-scrim" role="dialog" aria-modal="true" onClick={onClose}>
      <div
        className="modal modal--new-flow"
        onClick={(e) => e.stopPropagation()}
        data-testid="new-flow-modal"
      >
        <header className="modal__header">
          <div>
            <div className="modal__eyebrow">New integration flow</div>
            <h2 className="modal__title">Create a flow</h2>
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--icon-only"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <form onSubmit={create} className="modal__body modal__body--split">
          <section className="modal__col">
            <div className="modal__section-title">Starting point</div>
            <div className="starter-grid">
              {STARTERS.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  className={`starter-card ${starter === s.id ? 'is-selected' : ''} ${
                    s.available ? '' : 'is-disabled'
                  }`}
                  onClick={() => s.available && setStarter(s.id)}
                  disabled={!s.available}
                  title={s.available ? '' : 'Coming soon — track in /flows for now'}
                  data-testid={`starter-${s.id}`}
                >
                  <div className="starter-card__text">
                    <div className="starter-card__title">
                      {s.title}
                      {!s.available && (
                        <span className="starter-card__badge" style={{ marginLeft: 6 }}>
                          soon
                        </span>
                      )}
                    </div>
                    <div className="starter-card__desc">{s.desc}</div>
                  </div>
                  <div className="starter-card__radio" aria-hidden>
                    <span />
                  </div>
                </button>
              ))}
            </div>
          </section>

          <section className="modal__col">
            <div className="modal__section-title">Flow details</div>
            <div className="form-row form-row--stacked">
              <label className="form-row__label" htmlFor="new-flow-name">
                Name <span className="form-row__required">*</span>
              </label>
              <input
                id="new-flow-name"
                className="input"
                placeholder="e.g. Shopify → SAP Order Sync"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                maxLength={120}
                data-testid="new-flow-name"
                style={{
                  height: 32,
                  padding: '0 10px',
                  background: 'var(--bg-surface-2)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--r-sm)',
                  color: 'var(--text-1)',
                  fontSize: 13,
                }}
              />
              <div className="form-row__hint">
                Use a Source → Target pattern so it sorts well in the list.
              </div>
            </div>

            <div className="form-row form-row--stacked">
              <label className="form-row__label" htmlFor="new-flow-category">
                Category
              </label>
              <select
                id="new-flow-category"
                className="input"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={categories.length === 0}
                style={{
                  height: 32,
                  padding: '0 10px',
                  background: 'var(--bg-surface-2)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 'var(--r-sm)',
                  color: 'var(--text-1)',
                  fontSize: 13,
                }}
              >
                {categories.length === 0 && <option>(no categories — add some in Settings)</option>}
                {categories.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
              <div className="form-row__hint">
                Edit the catalogue in <strong style={{ color: 'var(--text-2)' }}>Settings → Flow categories</strong>.
                Categories don't yet attach to flows server-side — that lands when the marketplace API does.
              </div>
            </div>

            <div className="form-row form-row--stacked" style={{ marginTop: 'auto' }}>
              <div className="form-row__label">Initial state</div>
              <div className="t-3" style={{ fontSize: 12 }}>
                The flow starts as <strong style={{ color: 'var(--text-1)' }}>Draft</strong>.
                Open it in the designer, wire the steps, then deploy from the topbar.
              </div>
            </div>
          </section>

          <footer className="modal__footer" style={{ gridColumn: '1 / -1' }}>
            <span className="t-3" style={{ fontSize: 12 }}>
              You'll be taken to the designer to wire it up.
            </span>
            <div className="row" style={{ gap: 8, marginLeft: 'auto', display: 'flex' }}>
              <button type="button" className="btn btn--ghost" onClick={onClose}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn--primary"
                disabled={!canCreate}
                data-testid="new-flow-submit"
              >
                {creating ? 'Creating…' : 'Create & open designer'}
              </button>
            </div>
          </footer>
        </form>
      </div>
    </div>
  );
}
